import type { IOperationResultNoValue } from '../../../extViewmodels';
import type { IRevertImportResult } from '../../../models/AdminModels';
import { hasRevertWarnings } from './utils';

export type RevertResultState =
	| { kind: 'success'; result: IRevertImportResult }
	| { kind: 'warning'; result: IRevertImportResult }
	| { kind: 'error'; message: string };

interface RevertOperationResult {
	success?: boolean;
	value?: IRevertImportResult;
	systemMessage?: string;
}

/** Build the modal state from a resolved revert operation result (a fulfilled revert promise). */
export function revertResultFromOperation(opResult: RevertOperationResult): RevertResultState {
	if (!opResult.success) {
		return revertResultFromError(opResult);
	}
	if (!opResult.value) {
		return { kind: 'error', message: 'Revert succeeded but returned no details.' };
	}
	if (hasRevertWarnings(opResult.value)) {
		return { kind: 'warning', result: opResult.value };
	}
	return { kind: 'success', result: opResult.value };
}

/** Build the modal state from a rejected revert operation error. */
export function revertResultFromError(err: IOperationResultNoValue): RevertResultState {
	return { kind: 'error', message: err.systemMessage ?? 'An unknown error occurred.' };
}

type RevertErrorResolution = { showInModal: true; state: RevertResultState } | { showInModal: false };

/**
 * Decide how a rejected revert surfaces: a failure carrying a user-facing systemMessage is shown in
 * the RevertResultModal, anything else falls back to a generic API-error toast. Shared by the Imports
 * (function) and ImportHistory (class) containers so the branching cannot drift between them.
 */
export function resolveRevertError(err: IOperationResultNoValue): RevertErrorResolution {
	if (err.systemMessage) {
		return { showInModal: true, state: revertResultFromError(err) };
	}
	return { showInModal: false };
}
